import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { PhotoIdentite } from '../components/PhotoIdentite';
import { SignaturePictos } from '../components/PaiementPictos';
import { TooltipProvider } from '../components/ui/tooltip';
import { Badge } from '../components/ui/badge';
import { openBlobInNewTab } from '../utils/fileUtils';
import { useDocumentPreview } from '../components/DocumentPreviewModal';
import { PaiementsCandidatCard } from '../components/PaiementsCandidatCard';
import AdresseCandidatCard, { isAdresseComplete } from '../components/fiche-candidat/AdresseCandidatCard';
import { EnvoiTestsCandidat } from '../components/fiche-candidat/EnvoiTestsCandidat';
import { DevisPifDialog } from '../components/fiche-candidat/DevisPifDialog';
import { StructureStageCard } from '../components/fiche-candidat/StructureStageCard';
import { calculerAge } from '../utils/age';
import { DecisionCandidat } from '../components/DecisionCandidat';
import { BoutonEnvoiLivret } from '../components/livret-accueil/BoutonEnvoiLivret';
import { EmailsEnvoyesCard } from '../components/emails/EmailsEnvoyesCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  User, FileText, Calendar, ArrowLeft, Download, Eye, Trash2,
  Phone, Mail, Building2, Heart, UserPlus, CreditCard,
  CheckCircle, XCircle, AlertTriangle, Clock, Info, Euro, 
  FileCheck, FileX, Loader2, Send, FileSignature, GraduationCap, Shield, ClipboardList, Upload, Plus,
  FolderArchive
} from 'lucide-react';

const FicheCandidatCentre = ({ user }) => {
  const { previewDocument, DocumentModal } = useDocumentPreview();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const candidatId = searchParams.get('id');
  
  const [candidat, setCandidat] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zipLoading, setZipLoading] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(null);
  const [signatures, setSignatures] = useState(null);
  const [pifData, setPifData] = useState(null);
  const [conventionData, setConventionData] = useState(null);
  const [archives, setArchives] = useState([]);
  
  // Devis CRM
  const [devisData, setDevisData] = useState(null);
  const [devisProspectId, setDevisProspectId] = useState(null);
  const [numeroCandidatCRM, setNumeroCandidatCRM] = useState(null);
  const [generatingDevis, setGeneratingDevis] = useState(false);
  const [showDevisPifDialog, setShowDevisPifDialog] = useState(false);
  const [sendingDevis, setSendingDevis] = useState(false);
  const [sendingRelance, setSendingRelance] = useState(null);

  // Autres conventions
  const [autresConventions, setAutresConventions] = useState([]);
  const [uploadingConvention, setUploadingConvention] = useState(false);
  const [newConventionLabel, setNewConventionLabel] = useState('');
  const [showAddConvention, setShowAddConvention] = useState(false);
  
  // Modal validation document
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [motifRefus, setMotifRefus] = useState('');
  const [validating, setValidating] = useState(false);
  
  // Modal tests
  const [showTestsModal, setShowTestsModal] = useState(false);
  const [testsData, setTestsData] = useState(null);
  const [testsFormData, setTestsFormData] = useState({
    epreuve_vnm_bras: '',
    epreuve_vnm_selle: '',
    epreuve_entretien: '',
    resultat_global: '',
    centre_externe: '',
    date_tests_externe: '',
    commentaires: ''
  });
  const [savingTests, setSavingTests] = useState(false);

  useEffect(() => {
    if (candidatId) {
      loadCandidatData();
    }
  }, [candidatId]);

  const loadCandidatData = async () => {
    setLoading(true);
    try {
      // ENDPOINT AGRÉGÉ: 1 seule requête au lieu de ~10
      const res = await axios.get(`/candidats/${candidatId}/full`);
      const data = res.data;
      
      // Mapper les données de la réponse agrégée
      setCandidat(data.candidat);
      setDocuments(data.documents || []);
      setPaiements(data.paiements || []);
      setPifData(data.pif || null);
      setConventionData(data.convention && Object.keys(data.convention).length > 0 ? data.convention : null);
      setArchives(data.archives || []);
      setAutresConventions(data.autres_conventions || []);
      setSignatures(data.signatures || null);
      
      // Devis CRM
      if (data.devis) {
        setDevisData(data.devis);
        setDevisProspectId(data.prospect_id);
      }
      if (data.numero_candidat) {
        setNumeroCandidatCRM(data.numero_candidat);
      }
      
      // Version de la photo : recharge l'image quand le candidat la remplace
      const docs = data.documents || [];
      setPhotoVersion(docs.find(d => d.type_document === 'photo_identite')?.document_id || null);
      
      // Charger les tests si existants dans le profil
      if (data.candidat?.profil?.tests_selection) {
        const tests = data.candidat.profil.tests_selection;
        setTestsData(tests);
        setTestsFormData({
          epreuve_vnm_bras: tests.epreuve_vnm_bras || '',
          epreuve_vnm_selle: tests.epreuve_vnm_selle || '',
          epreuve_entretien: tests.epreuve_entretien || '',
          resultat_global: tests.resultat_global || '',
          centre_externe: tests.centre_externe || '',
          date_tests_externe: tests.date_tests_externe || '',
          commentaires: tests.commentaires || ''
        });
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateDocument = async (docId, statut) => {
    setValidating(true);
    try {
      await axios.put(`/candidats/documents/${docId}/valider`, {
        statut,
        motif_refus: statut === 'refuse' ? motifRefus : null
      });
      
      toast.success(statut === 'accepte' ? 'Document accepté' : 'Document refusé - Candidat notifié');
      setShowValidationModal(false);
      setSelectedDoc(null);
      setMotifRefus('');
      loadCandidatData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  const handleSaveTests = async () => {
    setSavingTests(true);
    try {
      await axios.put(`/candidats/${candidatId}/tests`, testsFormData);
      toast.success('Résultats enregistrés — pensez à les envoyer au candidat');
      setShowTestsModal(false);
      loadCandidatData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSavingTests(false);
    }
  };

  const handleValiderDossier = async () => {
    if (!window.confirm('Valider le dossier de ce candidat ? Il recevra une confirmation visuelle sur son espace.')) return;
    try {
      await axios.post(`/candidats/${candidatId}/valider-dossier`);
      toast.success('Dossier validé ! Le candidat verra la confirmation sur son espace.');
      loadCandidatData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const handleToggleEtape = async (etapeKey, currentValue) => {
    try {
      await axios.post(`/candidats/${candidatId}/valider-etape`, {
        etape: etapeKey,
        value: !currentValue
      });
      toast.success(!currentValue ? 'Etape validée' : 'Etape décochée');
      loadCandidatData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Fonctions pour le PIF
  const handleViewPIF = async () => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/pif/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erreur lors de l\'affichage du PIF');
    }
  };

  const handleDownloadPIF = async () => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/pif/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${candidat?.nom || ''}_${candidat?.prenom || ''}_PIF.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PIF');
    }
  };

  const handleDownloadPIFArchive = async () => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/pif/archive`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${candidat?.nom || ''}_${candidat?.prenom || ''}_PIF_archive.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Aucune archive disponible pour ce PIF');
    }
  };

  // Fonctions pour la Convention
  const handleCreateConvention = async () => {
    try {
      await axios.post(`/candidats/${candidatId}/convention`, {});
      toast.success('Convention créée avec succès');
      loadCandidatData();
    } catch (error) {
      toast.error('Erreur lors de la création de la convention');
    }
  };

  const handleViewConvention = async () => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/convention/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erreur lors de l\'affichage de la convention');
    }
  };

  const handleDownloadConvention = async () => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/convention/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${candidat?.nom || ''}_${candidat?.prenom || ''}_convention.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement de la convention');
    }
  };

  const handleSendConvention = async () => {
    try {
      await axios.post(`/candidats/${candidatId}/convention/send`);
      toast.success('Convention envoyée au candidat');
      loadCandidatData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la convention');
    }
  };

  const handleSendPIF = async () => {
    try {
      await axios.post(`/candidats/${candidatId}/pif/send`);
      toast.success('PIF envoyé au candidat');
      loadCandidatData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du PIF');
    }
  };

  const handleRelanceSignature = async (docType) => {
    setSendingRelance(docType);
    try {
      const res = await axios.post(`/candidats/${candidatId}/relance-signature`, { type: docType });
      toast.success(res.data.message || 'Email de relance envoyé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la relance');
    } finally {
      setSendingRelance(null);
    }
  };

  const handleDeletePIF = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer le PIF de ce candidat ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`/candidats/${candidatId}/pif`);
      toast.success('PIF supprimé avec succès');
      setPifData(null);
      loadCandidatData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression du PIF');
    }
  };

  const handleDeleteConvention = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer la convention de ce candidat ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`/candidats/${candidatId}/convention`);
      toast.success('Convention supprimée avec succès');
      setConventionData(null);
      loadCandidatData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression de la convention');
    }
  };

  const handleConvertirStagiaire = async () => {
    const dateEntree = window.prompt('Date d\'entrée en formation (AAAA-MM-JJ) :', new Date().toISOString().split('T')[0]);
    if (!dateEntree) return;
    if (!window.confirm(`Confirmer la conversion de ${candidat?.prenom} ${candidat?.nom} en stagiaire ?\n\nDate d'entrée : ${dateEntree}\n\nLe candidat sera transféré dans l'espace Stagiaires et n'apparaîtra plus dans la liste des candidats.`)) return;
    try {
      await axios.post(`/candidats/${candidatId}/convertir-stagiaire`, { date_entree_formation: dateEntree });
      toast.success('Candidat converti en stagiaire avec succès !');
      navigate('/gestion-stagiaires');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion');
    }
  };

  const handleViewDocument = async (documentId, filename) => {
    await previewDocument(documentId, filename);
  };

  const handleDownloadDocument = async (documentId, nomFichier) => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = axios.defaults.baseURL || '';
      const link = document.createElement('a');
      link.href = `${baseUrl}/documents/file/${documentId}?token=${token}`;
      link.setAttribute('download', nomFichier || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDownloadFiche = async () => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/fiche-inscription/pdf`, {
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${candidat?.nom || ''}_${candidat?.prenom || ''}_fiche_inscription.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  // === Tests de sélection PDF ===
  const handleViewTestsPDF = async () => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/tests/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erreur lors de la prévisualisation du PDF');
    }
  };

  const handleDownloadAttestation = async () => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/attestation/auto`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Attestation_tests_${candidat?.nom || ''}_${candidat?.prenom || ''}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Impossible de générer l'attestation");
    }
  };

  const handleDownloadDossierComplet = async () => {
    setZipLoading(true);
    try {
      const response = await axios.get(`/candidats/${candidatId}/dossier-complet-zip`, { responseType: 'blob', timeout: 180000 });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Dossier_candidat_${candidat?.nom || ''}_${candidat?.prenom || ''}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Dossier complet téléchargé');
    } catch (e) {
      toast.error('Erreur lors de la préparation du dossier');
    } finally {
      setZipLoading(false);
    }
  };

  const handleDownloadTestsPDF = async () => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/tests/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${candidat?.nom || ''}_${candidat?.prenom || ''}_tests_selection.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const handleArchiveTests = async () => {
    try {
      await axios.post(`/candidats/${candidatId}/tests/archive`);
      toast.success('Résultats archivés avec succès');
      loadCandidatData();
    } catch (error) {
      toast.error("Erreur lors de l'archivage");
    }
  };

  // === Convention archivage ===
  const handleArchiveConvention = async () => {
    try {
      await axios.post(`/candidats/${candidatId}/convention/archive`);
      toast.success('Convention archivée avec succès');
      loadCandidatData();
    } catch (error) {
      toast.error("Erreur lors de l'archivage");
    }
  };

  const handleViewArchive = async (archiveType) => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/archives/${archiveType}/view`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error("Erreur lors de l'ouverture de l'archive");
    }
  };

  const handleDownloadArchive = async (archiveType, filename) => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/archives/${archiveType}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `${candidat?.nom || ''}_${candidat?.prenom || ''}_archive.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erreur lors du téléchargement de l'archive");
    }
  };

  const handleArchivePIF = async () => {
    try {
      await axios.post(`/candidats/${candidatId}/pif/archive`);
      toast.success('PIF archivé avec succès');
      loadCandidatData();
    } catch (error) {
      toast.error("Erreur lors de l'archivage du PIF");
    }
  };

  // === Autres conventions ===
  const handleUploadAutreConvention = async (file) => {
    if (!newConventionLabel.trim()) {
      toast.error('Veuillez saisir un nom pour le document');
      return;
    }
    setUploadingConvention(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('label', newConventionLabel.trim());
      await axios.post(`/candidats/${candidatId}/autres-conventions/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Document "${newConventionLabel.trim()}" uploadé`);
      setNewConventionLabel('');
      setShowAddConvention(false);
      loadCandidatData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploadingConvention(false);
    }
  };

  const handleViewAutreConvention = async (docId) => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/autres-conventions/${docId}/view`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error("Erreur lors de l'ouverture du document");
    }
  };

  const handleDownloadAutreConvention = async (docId, filename) => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/autres-conventions/${docId}/view`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `${candidat?.nom || ''}_${candidat?.prenom || ''}_convention.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleDeleteAutreConvention = async (docId, label) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${label}" ?`)) return;
    try {
      await axios.delete(`/candidats/${candidatId}/autres-conventions/${docId}`);
      toast.success('Document supprimé');
      loadCandidatData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // === Devis handlers ===
  const handleGenerateDevisFromPif = async (options) => {
    setGeneratingDevis(true);
    try {
      const res = await axios.post(`/candidats/${candidatId}/generate-devis-from-pif`, options);
      toast.success(res.data.message || "Devis généré avec succès");
      setShowDevisPifDialog(false);
      loadCandidatData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la génération du devis");
    } finally {
      setGeneratingDevis(false);
    }
  };

  const handleDeleteDevis = async () => {
    if (!devisData || !devisProspectId) return;
    const signe = devisData.statut === 'signe' || devisData.statut === 'accepte';
    if (!window.confirm(`${signe ? 'Ce devis est signé. ' : ''}Supprimer définitivement ce devis ? Les factures liées seront aussi supprimées.`)) return;
    try {
      await axios.delete(`/crm/prospects/${devisProspectId}/devis/${devisData.devis_id}`);
      toast.success('Devis supprimé');
      setDevisData(null);
      loadCandidatData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleViewDevis = async () => {
    if (!devisData || !devisProspectId) return;
    try {
      const response = await axios.get(`/crm/prospects/${devisProspectId}/devis/${devisData.devis_id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      openBlobInNewTab(blob);
    } catch (error) {
      toast.error("Erreur lors de la prévisualisation");
    }
  };

  const handleDownloadDevis = async () => {
    if (!devisData || !devisProspectId) return;
    try {
      const response = await axios.get(`/crm/prospects/${devisProspectId}/devis/${devisData.devis_id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Devis_${numeroCandidatCRM || ''}_${candidat?.nom}_${candidat?.prenom}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleSendDevis = async () => {
    if (!devisData || !candidatId) return;
    setSendingDevis(true);
    try {
      await axios.post(`/candidats/${candidatId}/devis/send`);
      toast.success("Devis envoyé au candidat pour signature");
      loadCandidatData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingDevis(false);
    }
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'inscrit': return <Badge className="bg-blue-100 text-blue-700 pointer-events-none">Inscrit</Badge>;
      case 'tests_payes': return <Badge className="bg-amber-100 text-amber-700 pointer-events-none">Tests payés</Badge>;
      case 'tests_passes': return <Badge className="bg-purple-100 text-purple-700 pointer-events-none">Tests passés</Badge>;
      case 'accepte': return <Badge className="bg-green-100 text-green-700 pointer-events-none">Accepté</Badge>;
      case 'refuse': return <Badge className="bg-red-100 text-red-700 pointer-events-none">Refusé</Badge>;
      case 'abandon': return <Badge className="bg-slate-200 text-slate-700 pointer-events-none">Abandon</Badge>;
      default: return <Badge className="bg-slate-100 text-slate-700 pointer-events-none">{statut}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non renseigné';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(montant || 0);
  };

  // Calculer le total payé
  const totalPaye = paiements
    .filter(p => p.statut === 'completed')
    .reduce((sum, p) => sum + (p.montant || 0), 0);

  const hasPayedTests = paiements.some(p => p.type === 'tests_selection' && p.statut === 'completed');
  const hasPayedPrepa = paiements.some(p => p.type === 'journee_preparation' && p.statut === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#8DC63F]" />
      </div>
    );
  }

  if (!candidat) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Candidat non trouvé</p>
        <Link to="/gestion-candidats">
          <Button variant="link">Retour à la liste</Button>
        </Link>
      </div>
    );
  }

  const hasConvention = conventionData && Object.keys(conventionData).length > 0 && conventionData.statut;
  const profil = candidat.profil || {};
  const questions = profil.questions || {};

  // Documents requis pour candidat (même liste que DashboardCandidat)
  const DOCUMENTS_REQUIS_CANDIDAT = [
    { id: 'cni_passeport', nom: 'Carte d\'identité / Passeport', obligatoire: true },
    { id: 'cv', nom: 'CV', obligatoire: true },
    { id: 'psc', nom: 'PSC', obligatoire: true },
    { id: 'certificat_medical', nom: 'Certificat médical', obligatoire: true },
    { id: 'photo_identite', nom: 'Photo d\'identité', obligatoire: true },
    { id: 'attestation_natation', nom: 'Attestation natation 100m', obligatoire: true },
    { id: 'attestation_recensement', nom: 'Attestation de recensement', obligatoire: false },
    { id: 'permis_cotier', nom: 'Permis côtier', obligatoire: true },
    { id: 'permis_fluvial', nom: 'Permis fluvial', obligatoire: false },
    { id: 'crr', nom: 'CRR', obligatoire: false },
    { id: 'signature', nom: 'Signature', obligatoire: true },
  ];

  const getDocForType = (typeId) => documents.find(d => d.type_document === typeId);
  const documentsManquants = DOCUMENTS_REQUIS_CANDIDAT.filter(d => d.obligatoire && !getDocForType(d.id));
  const documentsFournis = documents.length;

  return (
    <div className="space-y-6" data-testid="fiche-candidat-centre">
      {/* Header Bandeau */}
      <div className="bg-gradient-to-r from-[#8DC63F] to-[#6ba32e] text-white rounded-lg p-6 shadow-md">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/gestion-candidats">
              <Button variant="outline" size="sm" className="bg-white/20 border-white/30 text-white hover:bg-white/30">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Retour
              </Button>
            </Link>
            
            {/* Photo */}
            <div className="w-16 h-20 bg-white/20 rounded-lg flex items-center justify-center border-4 border-white/50 overflow-hidden">
              <PhotoIdentite
                userId={candidatId}
                version={photoVersion}
                alt={`Photo ${candidat.prenom} ${candidat.nom}`}
                className="w-full h-full object-cover"
                testId="fiche-candidat-photo"
                fallback={<span className="text-2xl font-bold">{candidat.prenom?.[0]}{candidat.nom?.[0]}</span>}
              />
            </div>
            
            <div>
              <div className="flex items-center gap-2 mb-1">
                {getStatutBadge(candidat.statut_candidat)}
                <Badge className="bg-white/20">Candidat</Badge>
                {numeroCandidatCRM && (
                  <Badge className="bg-white/30 text-white font-mono" data-testid="badge-numero-candidat">N°{numeroCandidatCRM}</Badge>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold">
                {profil.genre || ''} {candidat.prenom} {candidat.nom}
              </h1>
              <p className="text-white/80 text-sm">
                Inscrit le {formatDate(candidat.created_at)} - FISL {candidat.centre === 'occitanie' ? 'Occitanie' : 'Atlantique'}
              </p>
              {candidat.statut_candidat === 'abandon' && candidat.motif_abandon && (
                <p className="text-sm mt-1 bg-white/15 rounded px-2 py-1 inline-block" data-testid="fiche-motif-abandon">Motif de l'abandon : {candidat.motif_abandon}</p>
              )}
              {signatures && Object.values(signatures).some(Boolean) && (
                <TooltipProvider>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="fiche-candidat-signatures">
                    <SignaturePictos signatures={signatures} userId={candidatId} />
                  </div>
                </TooltipProvider>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleDownloadFiche}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              variant="outline"
              data-testid="btn-download-fiche-header"
            >
              <Download className="h-4 w-4 mr-2" />
              Fiche d'inscription PDF
            </Button>
            <Button 
              onClick={handleDownloadDossierComplet}
              disabled={zipLoading}
              className="bg-white text-[#0F172A] hover:bg-slate-100"
              data-testid="btn-download-dossier-complet"
            >
              <FolderArchive className="h-4 w-4 mr-2" />
              {zipLoading ? 'Préparation...' : 'Dossier complet (ZIP)'}
            </Button>
            <BoutonEnvoiLivret candidatId={candidatId} />
            <DecisionCandidat candidatId={candidatId} statut={candidat.statut_candidat} motif={candidat.motif_abandon} header
              onChanged={(d) => setCandidat(prev => ({ ...prev, statut_candidat: d.statut_candidat, motif_abandon: d.motif_abandon }))} />
            {candidat.dossier_valide && (
              <Badge className="bg-green-100 text-green-700 px-3 py-1.5" data-testid="badge-dossier-valide">
                <CheckCircle className="h-3 w-3 mr-1" /> Dossier validé
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Validation du dossier - Checklist Centre */}
      <div className="bg-[#0F172A] rounded-xl p-5 shadow-lg" data-testid="validation-dossier-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#8DC63F]/20 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-[#8DC63F]" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Validation du dossier</h3>
              <p className="text-slate-400 text-xs">Cochez les étapes vérifiées</p>
            </div>
          </div>
          {candidat.dossier_valide && (
            <Badge className="bg-[#8DC63F] text-[#0F172A] font-bold px-3 py-1">Dossier validé</Badge>
          )}
        </div>
        <div className="space-y-2">
          {[
            { key: 'infos_administratives', label: 'Informations administratives', icon: '📋' },
            { key: 'pieces_fournir', label: 'Pièces à fournir', icon: '📎' },
            { key: 'paiement_frais', label: 'Paiement des frais', icon: '💳' },
          ].map(etape => {
            const done = candidat.dossier_etapes?.[etape.key] || false;
            return (
              <button
                key={etape.key}
                onClick={() => handleToggleEtape(etape.key, done)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left ${
                  done
                    ? 'bg-[#8DC63F]/15 border border-[#8DC63F]/30'
                    : 'bg-slate-800/60 border border-slate-700/50 hover:border-slate-600'
                }`}
                data-testid={`toggle-etape-${etape.key}`}
              >
                <div className={`h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0 ${
                  done ? 'bg-[#8DC63F]' : 'bg-slate-700 border border-slate-600'
                }`}>
                  {done && <CheckCircle className="h-4 w-4 text-[#0F172A]" />}
                </div>
                <span className={`text-sm font-medium ${done ? 'text-[#8DC63F]' : 'text-slate-300'}`}>
                  {etape.label}
                </span>
              </button>
            );
          })}
        </div>
        {/* Barre de progression */}
        {(() => {
          const steps = ['infos_administratives', 'pieces_fournir', 'paiement_frais'];
          const done = steps.filter(s => candidat.dossier_etapes?.[s]).length;
          const pct = Math.round((done / steps.length) * 100);
          return (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-slate-400">{done}/{steps.length} étapes validées</span>
                <span className={`font-bold ${pct === 100 ? 'text-[#8DC63F]' : 'text-slate-400'}`}>{pct}%</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div className={`h-2 rounded-full transition-all ${pct === 100 ? 'bg-[#8DC63F]' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })()}
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={documentsManquants.length > 0 ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}>
          <CardContent className="p-4 text-center">
            <p className={`text-2xl font-bold ${documentsManquants.length > 0 ? 'text-red-500' : 'text-green-600'}`}>
              {documentsFournis}/{DOCUMENTS_REQUIS_CANDIDAT.length}
            </p>
            <p className="text-sm text-slate-500">Documents</p>
            {documentsManquants.length > 0 && (
              <p className="text-xs text-red-500 mt-1">{documentsManquants.length} obligatoire(s) manquant(s)</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-500">{formatMontant(totalPaye)}</p>
            <p className="text-sm text-slate-500">Total payé</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className={`p-4 text-center ${hasPayedTests ? 'bg-green-50' : ''}`}>
            <p className="text-2xl font-bold">{hasPayedTests ? '✓' : '—'}</p>
            <p className="text-sm text-slate-500">Tests payés</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className={`p-4 text-center ${hasPayedPrepa ? 'bg-green-50' : ''}`}>
            <p className="text-2xl font-bold">{hasPayedPrepa ? '✓' : '—'}</p>
            <p className="text-sm text-slate-500">Journée prépa</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Colonne gauche: Infos personnelles + Documents */}
        <div className="space-y-6">
          {/* Validation du dossier d'inscription */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-lg">
                  <FileText className="h-5 w-5 text-[#8DC63F]" />
                  Validation du dossier d'inscription
                </div>
                {documentsManquants.length > 0 ? (
                  <Badge className="bg-red-100 text-red-700">{documentsManquants.length} manquant(s)</Badge>
                ) : (
                  <Badge className="bg-green-100 text-green-700">Complet</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {DOCUMENTS_REQUIS_CANDIDAT.map((docReq) => {
                  const uploaded = getDocForType(docReq.id);
                  const statut = uploaded?.statut;
                  
                  const bgClass = statut === 'accepte' ? 'bg-green-50 border-green-200' :
                                  statut === 'refuse' ? 'bg-red-50 border-red-200' :
                                  uploaded ? 'bg-amber-50 border-amber-200' :
                                  'bg-slate-50 border-slate-200';
                  
                  return (
                    <div key={docReq.id} className={`p-3 rounded-lg border ${bgClass}`} data-testid={`doc-row-${docReq.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          {statut === 'accepte' ? (
                            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                          ) : statut === 'refuse' ? (
                            <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                          ) : uploaded ? (
                            <Clock className="h-4 w-4 text-amber-500 shrink-0" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-slate-400 shrink-0" />
                          )}
                          <span className="text-sm font-medium text-slate-700 truncate">
                            {docReq.nom}
                            {docReq.obligatoire && <span className="text-red-400 ml-0.5">*</span>}
                          </span>
                        </div>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          statut === 'accepte' ? 'bg-green-100 text-green-700' :
                          statut === 'refuse' ? 'bg-red-100 text-red-700' :
                          uploaded ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {statut === 'accepte' ? 'Accepté' :
                           statut === 'refuse' ? 'Refusé' :
                           uploaded ? 'À valider' :
                           'Manquant'}
                        </span>
                      </div>
                      
                      {statut === 'refuse' && uploaded?.motif_refus && (
                        <p className="text-xs text-red-600 ml-6 mb-1">{uploaded.motif_refus}</p>
                      )}
                      
                      {uploaded ? (
                        <div className="flex items-center gap-1 ml-6 mt-1">
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-blue-600" onClick={() => handleViewDocument(uploaded.document_id)} title="Voir" data-testid={`btn-view-doc-${docReq.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-600" onClick={() => handleDownloadDocument(uploaded.document_id, uploaded.nom_fichier)} title="Télécharger" data-testid={`btn-download-doc-${docReq.id}`}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          {statut !== 'accepte' && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-green-600" onClick={() => handleValidateDocument(uploaded.document_id, 'accepte')} title="Accepter" data-testid={`btn-accept-doc-${docReq.id}`}>
                              <FileCheck className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {statut !== 'refuse' && statut !== 'accepte' && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => { setSelectedDoc(uploaded); setShowValidationModal(true); }} title="Refuser" data-testid={`btn-refuse-doc-${docReq.id}`}>
                              <FileX className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 ml-6 mt-1 italic">Non fourni par le candidat</p>
                      )}
                    </div>
                  );
                })}
              </div>
              {documentsManquants.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-700 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Documents obligatoires manquants :
                  </p>
                  <p className="text-xs text-red-600 mt-1 ml-6">
                    {documentsManquants.map(d => d.nom).join(', ')}
                  </p>
                </div>
              )}
              
              {/* Bouton envoyer récap email */}
              <div className="mt-4 flex justify-end">
                <Button 
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50 flex items-center gap-2"
                  data-testid="btn-envoyer-recap"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`/candidats/${candidatId}/envoyer-recap-documents`);
                      toast.success(res.data.message || 'Email récapitulatif envoyé !');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Erreur envoi email');
                    }
                  }}
                >
                  <Mail className="h-4 w-4" />
                  Envoyer un récap au candidat
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Informations personnelles */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5 text-[#8DC63F]" />
                Informations Personnelles
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-500">Nom complet</p>
                  <p className="font-medium">{profil.genre} {candidat.prenom} {candidat.nom}</p>
                </div>
                <div>
                  <p className="text-slate-500">Date de naissance</p>
                  <p className="font-medium" data-testid="fiche-date-naissance">
                    {formatDate(profil.date_naissance)}
                    {calculerAge(profil.date_naissance) !== null && (
                      <span className="ml-2 text-[#8DC63F] font-semibold" data-testid="fiche-age">({calculerAge(profil.date_naissance)} ans)</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Lieu de naissance</p>
                  <p className="font-medium">{profil.lieu_naissance || 'Non renseigné'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Nationalité</p>
                  <p className="font-medium">{profil.nationalite || 'Non renseigné'}</p>
                </div>
              </div>
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail className="h-4 w-4" />
                  <span>{candidat.email}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600 mt-1">
                  <Phone className="h-4 w-4" />
                  <span>{candidat.telephone || 'Non renseigné'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <StructureStageCard candidatId={candidatId} valeur={profil.structure_stage_envisagee} />

          {/* Adresse */}
          <AdresseCandidatCard candidatId={candidatId} profil={profil} onSaved={loadCandidatData} />

          {/* Informations médicales */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Heart className="h-5 w-5 text-[#8DC63F]" />
                Informations Médicales
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-500">Groupe sanguin</p>
                  <p className="font-medium">{profil.groupe_sanguin || 'Non renseigné'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Allergies</p>
                  <p className="font-medium">{profil.allergie || 'Aucune'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Situation de handicap</p>
                  <p className="font-medium">{profil.situation_handicap || 'Aucune'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Autres situations</p>
                  <p className="font-medium">{profil.autres_situations || 'Aucune'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact d'urgence */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <UserPlus className="h-5 w-5 text-[#8DC63F]" />
                Contact d'Urgence
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {profil.contact_urgence_nom ? (
                <>
                  <p className="font-medium">
                    {profil.contact_urgence_prenom} {profil.contact_urgence_nom}
                    {profil.contact_urgence_filiation && ` (${profil.contact_urgence_filiation})`}
                  </p>
                  <div className="flex items-center gap-2 text-slate-600 mt-1">
                    <Phone className="h-4 w-4" />
                    <span>{profil.contact_urgence_telephone || 'Non renseigné'}</span>
                  </div>
                </>
              ) : (
                <p className="text-slate-500 italic">Non renseigné</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Colonne droite: Questions, Paiements */}
        <div className="space-y-6">
          {/* Questions complémentaires */}
          <Card className="border-2 border-[#8DC63F]/30">
            <CardHeader className="bg-[#8DC63F]/5">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Info className="h-5 w-5 text-[#8DC63F]" />
                Informations complémentaires
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Titulaire d'un BPJEPS</span>
                <span className="font-medium">
                  {questions.titulaire_bpjeps ? (
                    <span className="text-green-600">
                      Oui - {questions.bpjeps_lequel} ({questions.bpjeps_annee})
                    </span>
                  ) : 'Non'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Titulaire tests sélection</span>
                <span className="font-medium">
                  {questions.titulaire_tests_selection ? 
                    <span className="text-green-600">Oui</span> : 'Non'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Date tests souhaitée</span>
                <span className="font-medium">{questions.date_tests_selection || 'Non choisie'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Résultats des tests de sélection */}
          <Card className="border-2 border-purple-200 bg-purple-50/30" data-testid="tests-selection-card">
            <CardHeader className="bg-purple-100/50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-lg">
                  <FileText className="h-5 w-5 text-purple-600" />
                  Résultats des tests de sélection
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowTestsModal(true)} className="border-purple-300 text-purple-700 hover:bg-purple-100" data-testid="btn-tests">
                  <FileText className="h-4 w-4 mr-1" />
                  {testsData ? 'Modifier' : 'Saisir les tests'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3 text-sm">
              {testsData && (testsData.epreuve_vnm_bras || testsData.epreuve_vnm_selle || testsData.epreuve_entretien || testsData.resultat_global) ? (
                <>
                  <EnvoiTestsCandidat candidatId={candidatId} tests={testsData} onEnvoye={loadCandidatData} />
                  {testsData.archive_date && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-2" data-testid="tests-archive-info">
                      <Shield className="h-5 w-5 text-green-600 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800">PDF archivé</p>
                        <p className="text-xs text-green-600">
                          Archivé le {new Date(testsData.archive_date).toLocaleDateString('fr-FR')} par {testsData.archive_par}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleViewArchive('tests_selection')} className="text-green-700 hover:bg-green-100" data-testid="tests-archive-view">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadArchive('tests_selection', `Tests_${candidat?.nom}_${candidat?.prenom}.pdf`)} className="text-green-700 hover:bg-green-100" data-testid="tests-archive-download">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {testsData.date_enregistrement && (
                    <div className="flex justify-between pb-2 border-b">
                      <span className="text-slate-500">Date d'enregistrement</span>
                      <span className="font-medium">{formatDate(testsData.date_enregistrement)}</span>
                    </div>
                  )}
                  {testsData.epreuve_vnm_bras && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Épreuve VNM à bras</span>
                      <Badge className={testsData.epreuve_vnm_bras === 'admis' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {testsData.epreuve_vnm_bras.toUpperCase()}
                      </Badge>
                    </div>
                  )}
                  {testsData.epreuve_vnm_selle && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Épreuve VNM à selle</span>
                      <Badge className={testsData.epreuve_vnm_selle === 'admis' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {testsData.epreuve_vnm_selle.toUpperCase()}
                      </Badge>
                    </div>
                  )}
                  {testsData.epreuve_entretien && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Entretien</span>
                      <Badge className={testsData.epreuve_entretien === 'admis' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {testsData.epreuve_entretien.toUpperCase()}
                      </Badge>
                    </div>
                  )}
                  {testsData.resultat_global && (
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-slate-700 font-medium">Résultat global</span>
                      <Badge className={testsData.resultat_global === 'admis' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}>
                        {testsData.resultat_global.toUpperCase()}
                      </Badge>
                    </div>
                  )}
                  {testsData.centre_externe && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Centre externe</span>
                      <span className="font-medium">{testsData.centre_externe}</span>
                    </div>
                  )}
                  {testsData.date_tests_externe && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Date tests externe</span>
                      <span className="font-medium">{testsData.date_tests_externe}</span>
                    </div>
                  )}
                  {testsData.commentaires && (
                    <div className="pt-2 border-t">
                      <p className="text-slate-500 text-xs mb-1">Commentaires :</p>
                      <p className="text-slate-700">{testsData.commentaires}</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 pt-3 border-t">
                    <Button variant="outline" size="sm" onClick={handleViewTestsPDF} className="border-purple-300 text-purple-700 hover:bg-purple-100" data-testid="btn-view-tests-pdf">
                      <Eye className="h-4 w-4 mr-1" />
                      Prévisualiser
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleDownloadTestsPDF} className="border-purple-300 text-purple-700 hover:bg-purple-100" data-testid="btn-download-tests-pdf">
                      <Download className="h-4 w-4 mr-1" />
                      Télécharger
                    </Button>
                    {testsData.resultat_global && (
                      <Button variant="outline" size="sm" onClick={handleDownloadAttestation} className="border-green-300 !text-green-700 hover:bg-green-50" data-testid="btn-download-attestation">
                        <FileCheck className="h-4 w-4 mr-1" />
                        Attestation {['admis', 'recu', 'reussi'].includes((testsData.resultat_global || '').toLowerCase()) ? 'de réussite' : 'de passage'}
                      </Button>
                    )}
                    {!testsData.archive_date && (
                      <Button variant="outline" size="sm" onClick={handleArchiveTests} className="border-green-300 text-green-700 hover:bg-green-100" data-testid="btn-archive-tests">
                        <Shield className="h-4 w-4 mr-1" />
                        Archiver
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-slate-500 italic text-center py-2">Aucun résultat de test enregistré pour ce candidat.</p>
              )}
            </CardContent>
          </Card>

          {/* Plan Individuel de Formation (PIF) */}
          <Card className="border-2 border-purple-200 bg-purple-50/30">
            <CardHeader className="bg-purple-100/50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-lg">
                  <FileCheck className="h-5 w-5 text-purple-600" />
                  Plan Individuel de Formation (PIF)
                </div>
                {pifData?.archive_date ? (
                  <Badge className="bg-green-500 text-white">Archivé</Badge>
                ) : pifData?.statut === 'signe' ? (
                  <Badge className="bg-blue-500 text-white">Signé par le candidat</Badge>
                ) : pifData?.statut === 'en_attente_signature' ? (
                  <Badge className="bg-amber-500 text-white">Envoyé</Badge>
                ) : pifData ? (
                  <Badge className="bg-purple-500 text-white">Créé</Badge>
                ) : (
                  <Badge variant="outline" className="text-slate-500">Non créé</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              {!pifData ? (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500 mb-3">Aucun PIF créé pour ce candidat.</p>
                  <Button onClick={() => navigate(`/pif-candidat?id=${candidatId}`)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="btn-create-pif">
                    <FileText className="h-4 w-4 mr-2" />
                    Créer
                  </Button>
                </div>
              ) : (
                <>
                  {pifData.archive_date && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="pif-archive-info">
                      <Shield className="h-5 w-5 text-green-600 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800">PDF archivé</p>
                        <p className="text-xs text-green-600">
                          Archivé le {new Date(pifData.archive_date).toLocaleDateString('fr-FR')} par {pifData.archive_par}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleViewArchive('pif')} className="text-green-700 hover:bg-green-100" data-testid="pif-archive-view">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadArchive('pif', `${candidat?.nom}_${candidat?.prenom}_PIF.pdf`)} className="text-green-700 hover:bg-green-100" data-testid="pif-archive-download">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {pifData.statut === 'en_attente_signature' && !pifData.archive_date && (
                    <p className="text-sm text-amber-600">En attente de signature par le candidat.</p>
                  )}
                  {pifData.statut === 'signe' && !pifData.archive_date && (
                    <p className="text-sm text-green-600">Signé par le candidat.</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={handleViewPIF} className="border-purple-300 text-purple-700 hover:bg-purple-100" data-testid="btn-view-pif" title="Prévisualiser">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleDownloadPIF} className="border-purple-300 text-purple-700 hover:bg-purple-100" data-testid="btn-download-pif">
                      <Download className="h-4 w-4 mr-1" />
                      Télécharger
                    </Button>
                    {pifData.statut !== 'en_attente_signature' && pifData.statut !== 'signe' && (
                      <Button variant="outline" size="sm" onClick={handleSendPIF} className="border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="btn-send-pif">
                        <Send className="h-4 w-4 mr-1" />
                        Envoyer au candidat
                      </Button>
                    )}
                    {pifData.statut === 'en_attente_signature' && (
                      <Button variant="outline" size="sm" onClick={() => handleRelanceSignature('pif')} disabled={sendingRelance === 'pif'} className="border-orange-300 text-orange-700 hover:bg-orange-100" data-testid="btn-relance-pif">
                        {sendingRelance === 'pif' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Mail className="h-4 w-4 mr-1" />}
                        Relancer par email
                      </Button>
                    )}
                    {!pifData.archive_date && (
                      <Button variant="outline" size="sm" onClick={handleArchivePIF} className="border-green-300 text-green-700 hover:bg-green-100" data-testid="btn-archive-pif">
                        <Shield className="h-4 w-4 mr-1" />
                        Archiver
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={handleDeletePIF} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="btn-delete-pif">
                      <Trash2 className="h-4 w-4 mr-1" />
                      Supprimer
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <DevisPifDialog open={showDevisPifDialog} onOpenChange={setShowDevisPifDialog} centre={candidat.centre} devis={devisData?.genere_depuis_pif ? devisData : null} onGenerate={handleGenerateDevisFromPif} loading={generatingDevis} />

          {/* Devis de formation */}
          <Card className="border-2 border-teal-200 bg-teal-50/30" data-testid="devis-section">
            <CardHeader className="bg-teal-100/50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-lg">
                  <FileText className="h-5 w-5 text-teal-600" />
                  Devis de formation
                </div>
                {devisData?.statut === 'en_attente_signature' ? (
                  <Badge className="bg-amber-500 text-white">En attente de signature</Badge>
                ) : devisData?.statut === 'signe' ? (
                  <Badge className="bg-green-500 text-white">Signé le {devisData.date_signature}</Badge>
                ) : devisData ? (
                  <Badge className="bg-teal-500 text-white">
                    {devisData.genere_depuis_pif ? 'Généré depuis PIF' : 'Brouillon'}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-slate-500">Non généré</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              {!devisData ? (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500 mb-3">Aucun devis disponible pour ce candidat.</p>
                  {pifData && (
                    <Button
                      onClick={() => setShowDevisPifDialog(true)}
                      disabled={generatingDevis}
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                      data-testid="btn-generate-devis-pif"
                    >
                      {generatingDevis ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                      Générer devis depuis le PIF
                    </Button>
                  )}
                  {!pifData && (
                    <p className="text-xs text-slate-400 mt-2">Créez d'abord un PIF pour générer un devis automatiquement.</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="bg-white rounded-lg p-3 border border-teal-100 text-sm space-y-1" data-testid="devis-info">
                    <p><span className="text-slate-500">N° Devis :</span> <span className="font-medium">{devisData.devis_numero || devisData.devis_id}</span></p>
                    <p><span className="text-slate-500">Date :</span> <span className="font-medium">{devisData.date_creation ? new Date(devisData.date_creation).toLocaleDateString('fr-FR') : '-'}</span></p>
                    <p><span className="text-slate-500">Montant :</span> <span className="font-semibold text-teal-700">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(devisData.montant_total || 0)}</span></p>
                    {devisData.taux_horaire > 0 && (
                      <p><span className="text-slate-500">Taux horaire :</span> <span className="font-medium">{devisData.taux_horaire} €/h</span></p>
                    )}
                    {devisData.devis_partage && (
                      <div className="pt-1 border-t border-teal-50 mt-1" data-testid="devis-repartition">
                        <p className="text-slate-500">Répartition :</p>
                        {[
                          ...(devisData.facturation?.financeurs || (devisData.facturation?.financeur?.actif ? [devisData.facturation.financeur] : []))
                            .map(f => [`Financeur${f.type_financement ? ` ${f.type_financement}` : ''} (${f.nom})`, f.montant]),
                          ...(devisData.facturation?.entreprise?.actif ? [[`Structure (${devisData.facturation.entreprise.nom})`, devisData.facturation.entreprise.montant]] : []),
                          ...(devisData.facturation?.stagiaire?.actif ? [['Reste à charge stagiaire', devisData.facturation.stagiaire.montant]] : []),
                        ].filter(([, m]) => m > 0).map(([label, m], i) => (
                          <p key={i}><span className="text-slate-500">{label} :</span> <span className="font-medium">{formatMontant(m)}</span></p>
                        ))}
                      </div>
                    )}
                    {devisData.bc_heures && Object.keys(devisData.bc_heures).length > 0 && (
                      <div className="pt-1 border-t border-teal-50 mt-1">
                        {Object.entries(devisData.bc_heures).map(([bc, h]) => (
                          <p key={bc}><span className="text-slate-500">{bc} :</span> <span className="font-medium">{h}h</span></p>
                        ))}
                        {devisData.nb_heures_foad > 0 && (
                          <p><span className="text-slate-500">FOAD :</span> <span className="font-medium">{devisData.nb_heures_foad}h</span></p>
                        )}
                        <p className="font-medium text-teal-600 pt-1">Total : {(devisData.nb_heures || 0) + (devisData.nb_heures_foad || 0)}h</p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={handleViewDevis} className="border-teal-300 text-teal-700 hover:bg-teal-100" data-testid="btn-view-devis" title="Prévisualiser">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleDownloadDevis} className="border-teal-300 text-teal-700 hover:bg-teal-100" data-testid="btn-download-devis">
                      <Download className="h-4 w-4 mr-1" />
                      Télécharger
                    </Button>
                    {devisData.statut !== 'en_attente_signature' && devisData.statut !== 'signe' && (
                      <Button variant="outline" size="sm" onClick={handleSendDevis} disabled={sendingDevis} className="border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="btn-send-devis">
                        {sendingDevis ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                        Envoyer pour signature
                      </Button>
                    )}
                    {devisData.statut === 'en_attente_signature' && (
                      <Button variant="outline" size="sm" onClick={() => handleRelanceSignature('devis')} disabled={sendingRelance === 'devis'} className="border-orange-300 text-orange-700 hover:bg-orange-100" data-testid="btn-relance-devis">
                        {sendingRelance === 'devis' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Mail className="h-4 w-4 mr-1" />}
                        Relancer par email
                      </Button>
                    )}
                    {pifData && (
                      <Button variant="outline" size="sm" onClick={() => setShowDevisPifDialog(true)} disabled={generatingDevis} className="border-teal-300 text-teal-600 hover:bg-teal-50" data-testid="btn-regenerate-devis">
                        {generatingDevis ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />}
                        Régénérer depuis PIF
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={handleDeleteDevis} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="btn-delete-devis" title="Supprimer le devis">
                      <Trash2 className="h-4 w-4 mr-1" />
                      Supprimer
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Convention de formation */}
          <Card className="border-2 border-blue-200 bg-blue-50/30">
            <CardHeader className="bg-blue-100/50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-lg">
                  <FileSignature className="h-5 w-5 text-blue-600" />
                  Convention de formation
                </div>
                {hasConvention?.archive_date ? (
                  <Badge className="bg-green-500 text-white">Archivée</Badge>
                ) : hasConvention && conventionData?.statut === 'signe' ? (
                  <Badge className="bg-blue-500 text-white">Signée par le candidat</Badge>
                ) : hasConvention && conventionData?.statut === 'en_attente_signature' ? (
                  <Badge className="bg-amber-500 text-white">Envoyée</Badge>
                ) : hasConvention ? (
                  <Badge className="bg-blue-500 text-white">Créée</Badge>
                ) : (
                  <Badge variant="outline" className="text-slate-500">Non créée</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              {!hasConvention ? (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500 mb-3">Aucune convention créée pour ce candidat.</p>
                  <Button variant="outline" size="sm" onClick={handleCreateConvention} className="border-blue-300 text-blue-700 hover:bg-blue-100" data-testid="btn-create-convention">
                    <FileSignature className="h-4 w-4 mr-1" />
                    Créer la convention
                  </Button>
                </div>
              ) : (
                <>
                  {conventionData?.archive_date && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="convention-archive-info">
                  <Shield className="h-5 w-5 text-green-600 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-800">PDF archivé</p>
                    <p className="text-xs text-green-600">
                      Archivée le {new Date(conventionData.archive_date).toLocaleDateString('fr-FR')} par {conventionData.archive_par}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleViewArchive('convention_formation')} className="text-green-700 hover:bg-green-100" data-testid="convention-archive-view">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDownloadArchive('convention_formation', `Convention_${candidat?.nom}_${candidat?.prenom}.pdf`)} className="text-green-700 hover:bg-green-100" data-testid="convention-archive-download">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              {conventionData?.statut !== 'signe' && !isAdresseComplete(profil) && (
                <p className="flex items-start gap-2 p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg" data-testid="convention-adresse-incomplete">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  Adresse du candidat incomplète : complétez-la (bouton « Modifier l'adresse ») avant d'envoyer la convention.
                </p>
              )}
              {conventionData?.statut === 'en_attente_signature' && !conventionData?.archive_date && (
                <p className="text-sm text-amber-600">En attente de signature par le candidat.</p>
              )}
              {conventionData?.statut === 'signe' && !conventionData?.archive_date && (
                <p className="text-sm text-green-600">Signée par le candidat.</p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={handleViewConvention} className="border-blue-300 text-blue-700 hover:bg-blue-100" data-testid="btn-view-convention" title="Prévisualiser">
                  <Eye className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleDownloadConvention} className="border-blue-300 text-blue-700 hover:bg-blue-100" data-testid="btn-download-convention">
                  <Download className="h-4 w-4 mr-1" />
                  Télécharger
                </Button>
                {conventionData?.statut !== 'en_attente_signature' && conventionData?.statut !== 'signe' && (
                  <Button variant="outline" size="sm" onClick={handleSendConvention} className="border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="btn-send-convention">
                    <Send className="h-4 w-4 mr-1" />
                    Envoyer au candidat
                  </Button>
                )}
                {conventionData?.statut === 'en_attente_signature' && (
                  <Button variant="outline" size="sm" onClick={() => handleRelanceSignature('convention')} disabled={sendingRelance === 'convention'} className="border-orange-300 text-orange-700 hover:bg-orange-100" data-testid="btn-relance-convention">
                    {sendingRelance === 'convention' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Mail className="h-4 w-4 mr-1" />}
                    Relancer par email
                  </Button>
                )}
                {!conventionData?.archive_date && (
                  <Button variant="outline" size="sm" onClick={handleArchiveConvention} className="border-green-300 text-green-700 hover:bg-green-100" data-testid="btn-archive-convention">
                    <Shield className="h-4 w-4 mr-1" />
                    Archiver
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleDeleteConvention} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="btn-delete-convention">
                  <Trash2 className="h-4 w-4 mr-1" />
                  Supprimer
                </Button>
              </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Autres conventions */}
          <Card className="border-2 border-orange-200 bg-orange-50/30" data-testid="autres-conventions-section">
            <CardHeader className="bg-orange-100/50">
              <CardTitle className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-lg">
                    <FileText className="h-5 w-5 text-orange-600" />
                    Autres conventions
                  </div>
                  <p className="text-xs font-normal text-orange-600 mt-1">apprentissage, transition pro., région...</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddConvention(true)}
                  className="border-orange-300 text-orange-700 hover:bg-orange-100"
                  data-testid="btn-add-convention"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              {/* Formulaire d'ajout */}
              {showAddConvention && (
                <div className="p-3 bg-white rounded-lg border-2 border-dashed border-orange-300 space-y-2" data-testid="add-convention-form">
                  <Input
                    placeholder="Nom du document (ex: Convention apprentissage, CERFA, Transition Pro...)"
                    value={newConventionLabel}
                    onChange={(e) => setNewConventionLabel(e.target.value)}
                    data-testid="input-convention-label"
                  />
                  <div className="flex gap-2">
                    <label className="cursor-pointer flex-1">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        onChange={(e) => { if (e.target.files[0]) handleUploadAutreConvention(e.target.files[0]); e.target.value = ''; }}
                        data-testid="input-upload-convention"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-orange-300 text-orange-700 hover:bg-orange-100 pointer-events-none"
                        disabled={uploadingConvention || !newConventionLabel.trim()}
                      >
                        {uploadingConvention ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                        Choisir le fichier et uploader
                      </Button>
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setShowAddConvention(false); setNewConventionLabel(''); }}
                      className="text-slate-500"
                      data-testid="btn-cancel-add-convention"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              )}

              {/* Liste des documents */}
              {autresConventions.length === 0 && !showAddConvention && (
                <p className="text-sm text-slate-400 text-center py-3">Aucun document ajouté</p>
              )}
              {autresConventions.map((doc) => (
                <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-100" data-testid={`convention-row-${doc.doc_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <FileSignature className="h-4 w-4 text-orange-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700">{doc.label}</p>
                      <p className="text-xs text-slate-500 truncate">{doc.filename}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => handleViewAutreConvention(doc.doc_id)} className="text-orange-700 hover:bg-orange-100" data-testid={`btn-view-${doc.doc_id}`} title="Prévisualiser">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDownloadAutreConvention(doc.doc_id, doc.filename)} className="text-orange-700 hover:bg-orange-100" data-testid={`btn-download-${doc.doc_id}`} title="Télécharger">
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteAutreConvention(doc.doc_id, doc.label)} className="text-red-500 hover:bg-red-50" data-testid={`btn-delete-${doc.doc_id}`} title="Supprimer">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Paiements */}
          <PaiementsCandidatCard candidatId={candidatId} paiements={paiements} onChange={setPaiements} faitPrepa={profil.fait_prepa} />
        </div>
      </div>

      <EmailsEnvoyesCard candidat={candidat} />

      {/* Prêt pour la formation - pleine largeur sous les colonnes */}
      {pifData?.statut === 'signe' && conventionData?.statut === 'signe' && (
        <Card className="border-2 border-emerald-400 bg-gradient-to-r from-emerald-50 to-green-50 shadow-lg" data-testid="convert-stagiaire-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-100 rounded-full">
                  <GraduationCap className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-emerald-800">Prêt pour la formation</h3>
                  <p className="text-sm text-emerald-600">PIF signé et Convention signée — ce candidat peut passer en stagiaire</p>
                </div>
              </div>
              <Button 
                onClick={handleConvertirStagiaire}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 text-base"
                data-testid="btn-convertir-stagiaire"
              >
                <GraduationCap className="h-5 w-5 mr-2" />
                Passer en Stagiaire
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal refus document */}
      <Dialog open={showValidationModal} onOpenChange={setShowValidationModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refuser le document</DialogTitle>
            <DialogDescription>
              Le candidat sera notifié du refus et devra renvoyer un document conforme.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Motif du refus *</Label>
            <Input
              value={motifRefus}
              onChange={(e) => setMotifRefus(e.target.value)}
              placeholder="Ex: Document illisible, date expirée..."
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidationModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={() => handleValidateDocument(selectedDoc?.document_id, 'refuse')}
              disabled={!motifRefus.trim() || validating}
              className="bg-red-500 hover:bg-red-600"
            >
              {validating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Refuser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal tests */}
      <Dialog open={showTestsModal} onOpenChange={setShowTestsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Résultats des tests de sélection</DialogTitle>
            <DialogDescription>
              Enregistrez les résultats des épreuves pour {candidat.prenom} {candidat.nom}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Épreuve VNM à bras</Label>
              <select
                value={testsFormData.epreuve_vnm_bras || ''}
                onChange={(e) => setTestsFormData({ ...testsFormData, epreuve_vnm_bras: e.target.value })}
                className="w-full h-10 px-3 border rounded-md"
              >
                <option value="">Non évalué</option>
                <option value="admis">ADMIS</option>
                <option value="ajourne">AJOURNÉ</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Épreuve VNM à selle</Label>
              <select
                value={testsFormData.epreuve_vnm_selle || ''}
                onChange={(e) => setTestsFormData({ ...testsFormData, epreuve_vnm_selle: e.target.value })}
                className="w-full h-10 px-3 border rounded-md"
              >
                <option value="">Non évalué</option>
                <option value="admis">ADMIS</option>
                <option value="ajourne">AJOURNÉ</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Entretien</Label>
              <select
                value={testsFormData.epreuve_entretien || ''}
                onChange={(e) => setTestsFormData({ ...testsFormData, epreuve_entretien: e.target.value })}
                className="w-full h-10 px-3 border rounded-md"
              >
                <option value="">Non évalué</option>
                <option value="admis">ADMIS</option>
                <option value="ajourne">AJOURNÉ</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Résultat global</Label>
              <select
                value={testsFormData.resultat_global || ''}
                onChange={(e) => setTestsFormData({ ...testsFormData, resultat_global: e.target.value })}
                className="w-full h-10 px-3 border rounded-md"
              >
                <option value="">Non évalué</option>
                <option value="admis">ADMIS</option>
                <option value="non_admis">NON ADMIS</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Centre externe (si applicable)</Label>
              <Input
                value={testsFormData.centre_externe || ''}
                onChange={(e) => setTestsFormData({ ...testsFormData, centre_externe: e.target.value })}
                placeholder="Nom du centre"
              />
            </div>
            <div className="space-y-2">
              <Label>Date tests externe (si applicable)</Label>
              <Input
                type="date"
                value={testsFormData.date_tests_externe || ''}
                onChange={(e) => setTestsFormData({ ...testsFormData, date_tests_externe: e.target.value })}
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Commentaires</Label>
              <textarea
                value={testsFormData.commentaires || ''}
                onChange={(e) => setTestsFormData({ ...testsFormData, commentaires: e.target.value })}
                className="w-full h-20 px-3 py-2 border rounded-md"
                placeholder="Notes et observations..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestsModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveTests} disabled={savingTests} className="bg-[#8DC63F] hover:bg-[#7db336]">
              {savingTests ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DocumentModal />
    </div>
  );
};

export default FicheCandidatCentre;
